import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { CheckCircle, Shield } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import servicePPF from "@/assets/service-ppf.jpg";
import coverageFrontEnd from "@/assets/coverage-front-end.jpg";
import coverageFullFront from "@/assets/coverage-full-front.jpg";
import coverageTrack from "@/assets/coverage-track.jpg";
import coverageFullVehicle from "@/assets/coverage-full-vehicle.jpg";

const coverageOptions = [
  {
    id: "front-end",
    name: "Front End Protection",
    description: "Hood, bumper, fenders, mirrors",
    image: coverageFrontEnd,
  },
  {
    id: "full-front",
    name: "Full Front Package",
    description: "Front + headlights + A-pillars",
    image: coverageFullFront,
  },
  {
    id: "track",
    name: "Track Package",
    description: "Front + rocker panels + rear fenders",
    image: coverageTrack,
  },
  {
    id: "full-vehicle",
    name: "Full Vehicle Wrap",
    description: "Complete coverage, every panel",
    image: coverageFullVehicle,
  },
];

const PPFSummerlin = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="PPF in Summerlin NV | Paint Protection Film | Battle Born Protection Films"
        description="Paint protection film for Summerlin, NV vehicles. XPEL-certified installation serving Summerlin drivers. Climate-controlled facility. Free quotes: (702) 559-9365."
        canonical="https://battlebornppf.com/ppf-in-summerlin"
      />
      <link rel="preload" as="image" href={servicePPF} />
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={servicePPF} 
            alt="Professional paint protection film installation for Summerlin NV vehicles at Battle Born PPF" 
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <Shield className="w-16 h-16 text-accent mb-6" />
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Paint Protection Film in Summerlin
            </h1>
            <p className="text-xl text-muted-foreground">
              XPEL Ultimate Plus self-healing film technology - The ultimate defense for your Summerlin vehicle's finish
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center">
              Why Paint Protection Film for Summerlin Vehicles?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Self-Healing Technology</h3>
                  <p className="text-muted-foreground">
                    Minor scratches and swirl marks disappear with heat, keeping your vehicle looking pristine
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Rock Chip Protection</h3>
                  <p className="text-muted-foreground">
                    8-mil thick film absorbs impact from road debris, preventing paint damage
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">UV & Chemical Resistant</h3>
                  <p className="text-muted-foreground">
                    Protects against sun damage, acid rain, bird droppings, and environmental contaminants
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Maintains Resale Value</h3>
                  <p className="text-muted-foreground">
                    Preserves factory paint condition, maximizing your vehicle's long-term value
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Invisible Protection</h3>
                  <p className="text-muted-foreground">
                    Crystal-clear finish maintains your vehicle's original appearance
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">10-Year Warranty</h3>
                  <p className="text-muted-foreground">
                    Manufacturer-backed warranty for peace of mind and long-term protection
                  </p>
                </div>
              </div>
            </div>

            {/* Coverage Options */}
            <div className="bg-card border border-border rounded-lg p-8">
              <h3 className="text-2xl font-heading font-bold mb-8">Coverage Options</h3>
              
              <Tabs defaultValue="front-end" className="w-full">
                <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8">
                  {coverageOptions.map((option) => (
                    <TabsTrigger key={option.id} value={option.id} className="text-sm">
                      {option.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {coverageOptions.map((option) => (
                  <TabsContent key={option.id} value={option.id} className="mt-0">
                    <div className="bg-black rounded-lg overflow-hidden">
                      <img 
                        src={option.image} 
                        alt={`${option.name} PPF coverage area diagram for Summerlin vehicles`}
                        className="w-full h-auto"
                        width="800"
                        height="450"
                        loading="lazy"
                      />
                      <div className="p-6 border-t border-border">
                        <h4 className="text-xl font-heading font-semibold mb-2">{option.name}</h4>
                        <p className="text-muted-foreground">{option.description}</p>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Protect Your Summerlin Vehicle Today
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Schedule a consultation to discuss the best protection package for your vehicle
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Paint Protection Film in Summerlin",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6960 W Russell Rd B",
              "addressLocality": "Las Vegas",
              "addressRegion": "NV",
              "postalCode": "89113",
              "addressCountry": "US"
            }
          },
          "areaServed": {
            "@type": "City",
            "name": "Summerlin"
          },
          "description": "Professional XPEL paint protection film installation in Summerlin providing self-healing technology, rock chip protection, and long-term paint preservation."
        })}
      </script>
    </div>
  );
};

export default PPFSummerlin;
